// pages/ShareRestaurant.jsx
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

const ShareRestaurant = () => {
  const { restaurantId } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRestaurant = async () => {
      try {
        const response = await api.get(`/restaurants/${restaurantId}`);
        setRestaurant(response.data.restaurant);
      } catch (error) {
        console.error('Error fetching restaurant:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRestaurant();
  }, [restaurantId]);

  // Update meta tags immediately when restaurant loads
  useEffect(() => {
    if (restaurant) {
      const restaurantUrl = `${window.location.origin}/restaurant/${restaurant.id}`;
      const description = `Check out ${restaurant.name_en || restaurant.name} menu on QR Table`;
      const ogImageUrl = restaurant.logo || restaurant.cover_image || `${window.location.origin}/default-og-image.jpg`;
      
      // Clear existing meta tags first
      const metaTags = [
        'description', 'og:title', 'og:description', 'og:image', 
        'og:url', 'og:type', 'og:site_name', 'twitter:card', 
        'twitter:title', 'twitter:description', 'twitter:image'
      ];
      
      metaTags.forEach(tag => {
        const selector = tag.startsWith('og:') ? `meta[property="${tag}"]` : `meta[name="${tag}"]`;
        const existingMeta = document.querySelector(selector);
        if (existingMeta) {
          existingMeta.remove();
        }
      });
      
      // Create new meta tags
      const createMetaTag = (name, content, isProperty = false) => {
        const meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      };
      
      // Set title
      document.title = `QR Table - ${restaurant.name_en || restaurant.name}`;
      
      // Create all meta tags
      createMetaTag('description', description);
      createMetaTag('og:title', `QR Table - ${restaurant.name_en || restaurant.name}`, true);
      createMetaTag('og:description', description, true);
      createMetaTag('og:image', ogImageUrl, true);
      createMetaTag('og:image:width', '1200', true);
      createMetaTag('og:image:height', '630', true);
      createMetaTag('og:url', restaurantUrl, true);
      createMetaTag('og:type', 'website', true);
      createMetaTag('og:site_name', 'QR Table', true);
      createMetaTag('twitter:card', 'summary_large_image');
      createMetaTag('twitter:title', `QR Table - ${restaurant.name_en || restaurant.name}`);
      createMetaTag('twitter:description', description);
      createMetaTag('twitter:image', ogImageUrl);
      
      // Add a canonical link
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', restaurantUrl);
    }
  }, [restaurant]);

  // Redirect to the actual restaurant page after a short delay
  useEffect(() => {
    if (restaurant) {
      const timer = setTimeout(() => {
        window.location.href = `/restaurant/${restaurant.id}`;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [restaurant]);

  // Don't return early - always render something
  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Loading...</div>;
  }

  if (!restaurant) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>Restaurant not found</div>;
  }

  return (
    <div style={{ textAlign: 'center', padding: '50px' }}>
      <h1>{restaurant.name_en || restaurant.name}</h1>
      <p>Redirecting to menu...</p>
    </div>
  );
};

export default ShareRestaurant;